import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Switch,
  FormControlLabel,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import BarChartIcon from "@mui/icons-material/BarChart";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import ArticleIcon from "@mui/icons-material/Article";
import PieChartIcon from "@mui/icons-material/PieChart";
import { useState, useMemo, useCallback, useRef } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Brush,
  Area,
  ComposedChart,
  BarChart as RechartsBarChart,
  Bar,
  ReferenceLine,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { alpha } from "@mui/material/styles";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const BROWN = "#210C03";
const ACCENT = "#00E5FF";
const COBALT = "#2D5BFF";

/* ============================================================================
 * Types
 * ========================================================================= */

export type EventTrendRow = {
  event_date: string;
  event_count: number;
};

type ChartStats = {
  total: number;
  avg: number;
  max: number;
  min: number;
  median: number;
  trend: "up" | "down" | "flat";
  trendPercent: number;
};

type ChartMode = "line" | "bar" | "pie";

type EventTrendChartProps = {
  data: EventTrendRow[];
  title?: string;
  showBrush?: boolean;
  height?: number;
  initialMode?: ChartMode;
};

const PIE_COLORS = [COBALT, ACCENT, "#FF9800", "#4CAF50", "#E91E63"];

function buildPieData(data: EventTrendRow[]) {
  return data.map(d => ({
    name: d.event_date,
    value: d.event_count,
  }));
}

/* ============================================================================
 * Type Guards
 * ========================================================================= */

export function isEventTrendData(content: unknown): content is EventTrendRow[] {
  if (!Array.isArray(content) || content.length === 0) return false;

  return content.every(row => {
    if (row == null || typeof row !== "object") return false;
    const r = row as Record<string, unknown>;

    const hasValidDate =
      typeof r.event_date === "string" && r.event_date.length > 0;

    const hasValidCount =
      typeof r.event_count === "number" ||
      (typeof r.event_count === "string" &&
        r.event_count.trim().length > 0 &&
        !Number.isNaN(Number(r.event_count)));

    return hasValidDate && hasValidCount;
  });
}

/* ============================================================================
 * Utilities
 * ========================================================================= */

function coerceCount(value: number | string): number {
  return typeof value === "number" ? value : Number(value);
}

function formatDateFromYYYYMMDD(value: string): string {
  if (value.length === 8 && /^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  return value;
}

function normalizeTrendData(rows: EventTrendRow[]): EventTrendRow[] {
  return rows
    .slice()
    .sort((a, b) => a.event_date.localeCompare(b.event_date))
    .map(row => ({
      event_date: formatDateFromYYYYMMDD(row.event_date),
      event_count: coerceCount(row.event_count),
    }));
}

function calculateStats(data: EventTrendRow[]): ChartStats {
  const counts = data.map(d => d.event_count);
  const total = counts.reduce((sum, c) => sum + c, 0);
  const sortedCounts = [...counts].sort((a, b) => a - b);
  const median = sortedCounts[Math.floor(sortedCounts.length / 2)];

  const quarterSize = Math.floor(counts.length / 4);
  const firstQuarter = counts.slice(0, quarterSize);
  const lastQuarter = counts.slice(-quarterSize);

  const firstAvg =
    firstQuarter.reduce((sum, c) => sum + c, 0) / firstQuarter.length || 0;
  const lastAvg =
    lastQuarter.reduce((sum, c) => sum + c, 0) / lastQuarter.length || 0;

  const trendPercent =
    firstAvg > 0 ? ((lastAvg - firstAvg) / firstAvg) * 100 : 0;
  const trend =
    Math.abs(trendPercent) < 5 ? "flat" : trendPercent > 0 ? "up" : "down";

  return {
    total,
    avg: total / counts.length,
    max: Math.max(...counts),
    min: Math.min(...counts),
    median,
    trend,
    trendPercent,
  };
}

function calculateTickInterval(
  dataLength: number,
  targetTicks: number = 12
): number {
  return Math.max(1, Math.floor(dataLength / targetTicks));
}

/* ============================================================================
 * Export Utilities – now accept an element instead of an id
 * ========================================================================= */

async function exportChartAsImage(
  element: HTMLElement,
  format: "png" | "jpg",
  filename: string
): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: "#ffffff",
    logging: false,
    useCORS: true,
    allowTaint: true,
  });

  const link = document.createElement("a");
  link.download = filename;

  if (format === "png") {
    link.href = canvas.toDataURL("image/png");
  } else {
    link.href = canvas.toDataURL("image/jpeg", 0.95);
  }

  link.click();
}

async function exportChartAsPDF(
  element: HTMLElement,
  filename: string
): Promise<void> {
  const canvas = await html2canvas(element, {
    scale: 2,
    backgroundColor: "#ffffff",
    logging: false,
    useCORS: true,
    allowTaint: true,
  });

  const imgData = canvas.toDataURL("image/png");

  const imgWidth = canvas.width;
  const imgHeight = canvas.height;
  const ratio = imgWidth / imgHeight;

  const pdfWidth = 297;
  const pdfHeight = 210;

  let width = pdfWidth;
  let height = pdfWidth / ratio;

  if (height > pdfHeight) {
    height = pdfHeight;
    width = pdfHeight * ratio;
  }

  const pdf = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const xOffset = (pdfWidth - width) / 2;
  const yOffset = (pdfHeight - height) / 2;

  pdf.addImage(imgData, "PNG", xOffset, yOffset, width, height);
  pdf.save(filename);
}

/* ============================================================================
 * Futuristic Glow Effects
 * ========================================================================= */

const glowKeyframes = {
  "@keyframes glow": {
    "0%, 100%": {
      boxShadow: `0 0 8px ${alpha(ACCENT, 0.3)}, 0 0 16px ${alpha(
        ACCENT,
        0.2
      )}`,
    },
    "50%": {
      boxShadow: `0 0 12px ${alpha(ACCENT, 0.5)}, 0 0 24px ${alpha(
        ACCENT,
        0.3
      )}`,
    },
  },
  "@keyframes pulseGlow": {
    "0%, 100%": { opacity: 0.6 },
    "50%": { opacity: 1 },
  },
};

/* ============================================================================
 * Sub-components
 * ========================================================================= */

function CustomTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;

  return (
    <Box
      sx={{
        bgcolor: "rgba(33, 12, 3, 0.95)",
        backdropFilter: "blur(12px)",
        border: `1px solid ${alpha(ACCENT, 0.3)}`,
        borderRadius: 2,
        p: 2,
        boxShadow: `0 8px 32px ${alpha(ACCENT, 0.3)}`,
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: -1,
          borderRadius: 2,
          padding: "1px",
          background: `linear-gradient(135deg, ${ACCENT}, ${COBALT})`,
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
        },
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          display: "block",
          mb: 0.5,
          color: ACCENT,
          fontFamily: "monospace",
          letterSpacing: 1,
        }}
      >
        {data.event_date}
      </Typography>
      <Typography
        variant="h6"
        sx={{
          color: "#fff",
          fontWeight: 800,
          fontFamily: "monospace",
        }}
      >
        {data.event_count.toLocaleString()}
        <Typography
          component="span"
          variant="caption"
          sx={{ ml: 0.5, opacity: 0.7 }}
        >
          events
        </Typography>
      </Typography>
    </Box>
  );
}

function FuturisticStatCard({
  label,
  value,
  color = COBALT,
  icon,
}: {
  label: string;
  value: string | number;
  color?: string;
  icon?: React.ReactNode;
}) {
  return (
    <Box
      sx={{
        position: "relative",
        px: 2,
        py: 1.5,
        borderRadius: 2,
        background: `linear-gradient(135deg, ${alpha(color, 0.08)}, ${alpha(
          color,
          0.03
        )})`,
        border: `1px solid ${alpha(color, 0.2)}`,
        overflow: "hidden",
        transition: "all 0.3s ease",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 24px ${alpha(color, 0.2)}`,
          borderColor: alpha(color, 0.4),
        },
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        {icon && <Box sx={{ color, opacity: 0.8 }}>{icon}</Box>}
        <Box flex={1}>
          <Typography
            variant="caption"
            sx={{
              opacity: 0.7,
              display: "block",
              textTransform: "uppercase",
              letterSpacing: 1,
              fontSize: 10,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color,
              fontFamily: "monospace",
            }}
          >
            {typeof value === "number" ? value.toLocaleString() : value}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

function TrendIndicator({
  trend,
  percent,
}: {
  trend: "up" | "down" | "flat";
  percent: number;
}) {
  const Icon =
    trend === "up"
      ? TrendingUpIcon
      : trend === "down"
      ? TrendingDownIcon
      : TrendingFlatIcon;
  const color =
    trend === "up"
      ? "#4caf50"
      : trend === "down"
      ? "#f44336"
      : alpha("#000", 0.5);

  return (
    <Chip
      icon={<Icon sx={{ fontSize: 18 }} />}
      label={`${trend === "up" ? "+" : ""}${percent.toFixed(1)}%`}
      size="small"
      sx={{
        bgcolor: alpha(color, 0.1),
        color,
        border: `1px solid ${alpha(color, 0.3)}`,
        fontWeight: 700,
        fontFamily: "monospace",
        "& .MuiChip-icon": {
          color,
        },
      }}
    />
  );
}

function StatsPanel({ stats }: { stats: ChartStats }) {
  return (
    <Stack direction="row" spacing={1.5} flexWrap="wrap">
      <FuturisticStatCard
        label="Total Events"
        value={stats.total}
        color={COBALT}
      />
      <FuturisticStatCard
        label="Avg/Day"
        value={stats.avg.toFixed(1)}
        color={BROWN}
      />
      <FuturisticStatCard label="Peak" value={stats.max} color={ACCENT} />
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: 2,
          border: `1px solid ${alpha("#000", 0.1)}`,
          display: "flex",
          alignItems: "center",
        }}
      >
        <TrendIndicator trend={stats.trend} percent={stats.trendPercent} />
      </Box>
    </Stack>
  );
}

function ZoomControls({
  onZoomIn,
  onZoomOut,
  onReset,
  canZoomIn,
  canReset,
  mode,
  onModeChange,
  showAverage,
  onToggleAverage,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  canZoomIn: boolean;
  canReset: boolean;
  mode: ChartMode;
  onModeChange: (mode: ChartMode) => void;
  showAverage: boolean;
  onToggleAverage: () => void;
}) {
  const zoomDisabled = mode === "pie";

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Line chart">
          <IconButton
            size="small"
            onClick={() => onModeChange("line")}
            sx={{
              border: `1px solid ${alpha(ACCENT, mode === "line" ? 0.8 : 0.3)}`,
              bgcolor: alpha(ACCENT, mode === "line" ? 0.2 : 0.08),
              color: ACCENT,
              "&:hover": {
                bgcolor: alpha(ACCENT, 0.25),
                boxShadow: `0 0 12px ${alpha(ACCENT, 0.3)}`,
              },
            }}
          >
            <ShowChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Bar chart">
          <IconButton
            size="small"
            onClick={() => onModeChange("bar")}
            sx={{
              border: `1px solid ${alpha(ACCENT, mode === "bar" ? 0.8 : 0.3)}`,
              bgcolor: alpha(ACCENT, mode === "bar" ? 0.2 : 0.08),
              color: ACCENT,
              "&:hover": {
                bgcolor: alpha(ACCENT, 0.25),
                boxShadow: `0 0 12px ${alpha(ACCENT, 0.3)}`,
              },
            }}
          >
            <BarChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Pie chart">
          <IconButton
            size="small"
            onClick={() => onModeChange("pie")}
            sx={{
              border: `1px solid ${alpha(ACCENT, mode === "pie" ? 0.8 : 0.3)}`,
              bgcolor: alpha(ACCENT, mode === "pie" ? 0.2 : 0.08),
              color: ACCENT,
              "&:hover": {
                bgcolor: alpha(ACCENT, 0.25),
                boxShadow: `0 0 12px ${alpha(ACCENT, 0.3)}`,
              },
            }}
          >
            <PieChartIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {mode !== "pie" && (
        <Tooltip title="Toggle average line">
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showAverage}
                onChange={onToggleAverage}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: ACCENT,
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    bgcolor: ACCENT,
                  },
                }}
              />
            }
            label={
              <Typography variant="caption" sx={{ fontSize: 10 }}>
                AVG
              </Typography>
            }
            sx={{ mr: 1 }}
          />
        </Tooltip>
      )}

      <Box sx={{ width: 1, height: 24, bgcolor: alpha("#000", 0.1) }} />

      <Tooltip title="Zoom in">
        <span>
          <IconButton
            size="small"
            onClick={onZoomIn}
            disabled={!canZoomIn || zoomDisabled}
            sx={{
              border: `1px solid ${alpha("#000", 0.1)}`,
              "&:hover": {
                bgcolor: alpha(COBALT, 0.06),
                borderColor: alpha(COBALT, 0.3),
              },
            }}
          >
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Zoom out">
        <span>
          <IconButton
            size="small"
            onClick={onZoomOut}
            disabled={zoomDisabled}
            sx={{
              border: `1px solid ${alpha("#000", 0.1)}`,
              "&:hover": {
                bgcolor: alpha(COBALT, 0.06),
                borderColor: alpha(COBALT, 0.3),
              },
            }}
          >
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <Tooltip title="Reset zoom">
        <span>
          <IconButton
            size="small"
            onClick={onReset}
            disabled={!canReset || zoomDisabled}
            sx={{
              border: `1px solid ${alpha("#000", 0.1)}`,
              "&:hover": {
                bgcolor: alpha(COBALT, 0.06),
                borderColor: alpha(COBALT, 0.3),
              },
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

/* ============================================================================
 * Main Component
 * ========================================================================= */

function EventTrendChart({
  data,
  showBrush = true,
  height = 400,
  initialMode = "line",
}: EventTrendChartProps) {
  const [zoomDomain, setZoomDomain] = useState<[number, number] | null>(null);
  const [mode, setMode] = useState<ChartMode>(initialMode);
  const [showAverage, setShowAverage] = useState(true);
  const [chartExportMenuAnchor, setChartExportMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [exportingChart, setExportingChart] = useState(false);

  const chartRef = useRef<HTMLDivElement | null>(null);

  const normalized = useMemo(() => normalizeTrendData(data), [data]);
  const stats = useMemo(() => calculateStats(normalized), [normalized]);

  const visibleData = useMemo(() => {
    if (!zoomDomain) return normalized;
    return normalized.slice(zoomDomain[0], zoomDomain[1] + 1);
  }, [normalized, zoomDomain]);

  const tickInterval = useMemo(
    () => calculateTickInterval(visibleData.length),
    [visibleData.length]
  );

  const handleChartExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setChartExportMenuAnchor(event.currentTarget);
  };

  const handleChartExportMenuClose = () => {
    setChartExportMenuAnchor(null);
  };

  const handleExportChart = async (format: "png" | "jpg" | "pdf") => {
    handleChartExportMenuClose();
    if (!chartRef.current) return;

    setExportingChart(true);
    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, "-");
    const baseFilename = `event-trend-chart-${timestamp}`;

    try {
      if (format === "pdf") {
        await exportChartAsPDF(chartRef.current, `${baseFilename}.pdf`);
      } else {
        await exportChartAsImage(
          chartRef.current,
          format,
          `${baseFilename}.${format}`
        );
      }
    } catch (error) {
      console.error("Chart export error:", error);
    } finally {
      setExportingChart(false);
    }
  };

  const handleZoomIn = useCallback(() => {
    const current = zoomDomain || [0, normalized.length - 1];
    const range = current[1] - current[0];
    const newRange = Math.max(7, Math.floor(range * 0.7));
    const center = Math.floor((current[0] + current[1]) / 2);
    const newStart = Math.max(0, center - Math.floor(newRange / 2));
    const newEnd = Math.min(normalized.length - 1, newStart + newRange);

    setZoomDomain([newStart, newEnd]);
  }, [zoomDomain, normalized.length]);

  const handleZoomOut = useCallback(() => {
    const current = zoomDomain || [0, normalized.length - 1];
    const range = current[1] - current[0];
    const newRange = Math.min(normalized.length - 1, Math.floor(range * 1.4));
    const center = Math.floor((current[0] + current[1]) / 2);
    const newStart = Math.max(0, center - Math.floor(newRange / 2));
    const newEnd = Math.min(normalized.length - 1, newStart + newRange);

    if (newStart === 0 && newEnd === normalized.length - 1) {
      setZoomDomain(null);
    } else {
      setZoomDomain([newStart, newEnd]);
    }
  }, [zoomDomain, normalized.length]);

  const handleReset = useCallback(() => {
    setZoomDomain(null);
  }, []);

  const handleBrushChange = useCallback((range: any) => {
    if (range?.startIndex !== undefined && range?.endIndex !== undefined) {
      setZoomDomain([range.startIndex, range.endIndex]);
    }
  }, []);

  const handleModeChange = useCallback((next: ChartMode) => {
    setMode(next);
  }, []);

  const handleToggleAverage = useCallback(() => {
    setShowAverage(prev => !prev);
  }, []);

  const canZoomIn = useMemo(() => {
    if (zoomDomain) {
      return zoomDomain[1] - zoomDomain[0] > 7;
    }
    return normalized.length > 7;
  }, [zoomDomain, normalized.length]);

  if (!normalized.length) {
    return null;
  }

  return (
    <Box
      sx={{ width: "100%", mt: 1, ...glowKeyframes }}
      ref={chartRef}
      // id removed – ref handles export targeting
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <StatsPanel stats={stats} />

        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Export chart">
            <IconButton
              size="small"
              onClick={handleChartExportMenuOpen}
              disabled={exportingChart}
              sx={{
                border: `1px solid ${alpha(COBALT, 0.3)}`,
                bgcolor: alpha(COBALT, 0.08),
                color: COBALT,
                "&:hover": {
                  bgcolor: alpha(COBALT, 0.15),
                  boxShadow: `0 0 12px ${alpha(COBALT, 0.3)}`,
                },
              }}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <ZoomControls
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onReset={handleReset}
            canZoomIn={canZoomIn}
            canReset={!!zoomDomain}
            mode={mode}
            onModeChange={handleModeChange}
            showAverage={showAverage}
            onToggleAverage={handleToggleAverage}
          />
        </Stack>
      </Stack>

      <Box
        sx={{
          width: "100%",
          height,
          bgcolor: alpha("#000", 0.02),
          borderRadius: 3,
          border: `1px solid ${alpha(COBALT, 0.15)}`,
          p: 2,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "2px",
            background: `linear-gradient(90deg, transparent, ${ACCENT}, ${COBALT}, transparent)`,
            animation: "pulseGlow 3s ease-in-out infinite",
          },
        }}
      >
        <ResponsiveContainer>
          {mode === "line" ? (
            <ComposedChart
              data={visibleData}
              margin={{ top: 8, right: 16, bottom: 60, left: 8 }}
            >
              <defs>
                <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COBALT} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={COBALT} stopOpacity={0.05} />
                </linearGradient>
                <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={COBALT} />
                  <stop offset="50%" stopColor={ACCENT} />
                  <stop offset="100%" stopColor={COBALT} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(COBALT, 0.08)}
                vertical={false}
              />

              <XAxis
                dataKey="event_date"
                tick={{ fontSize: 10, fill: alpha("#000", 0.7) }}
                tickLine={false}
                axisLine={{ stroke: alpha(COBALT, 0.2) }}
                interval={tickInterval}
                angle={-45}
                textAnchor="end"
                height={70}
              />

              <YAxis
                tick={{ fontSize: 11, fill: alpha("#000", 0.7) }}
                tickLine={false}
                axisLine={{ stroke: alpha(COBALT, 0.2) }}
              />

              <RechartsTooltip content={<CustomTooltip />} />

              {showAverage && (
                <ReferenceLine
                  y={stats.avg}
                  stroke={ACCENT}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `Avg: ${stats.avg.toFixed(1)}`,
                    fill: ACCENT,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
              )}

              <Area
                type="monotone"
                dataKey="event_count"
                stroke="none"
                fill="url(#colorCount)"
              />

              <Line
                type="monotone"
                dataKey="event_count"
                stroke="url(#lineGradient)"
                strokeWidth={3}
                dot={{
                  r: 4,
                  fill: "#fff",
                  stroke: COBALT,
                  strokeWidth: 2,
                }}
                activeDot={{
                  r: 7,
                  fill: ACCENT,
                  stroke: "#fff",
                  strokeWidth: 3,
                  filter: `drop-shadow(0 0 8px ${ACCENT})`,
                }}
              />
            </ComposedChart>
          ) : mode === "bar" ? (
            <RechartsBarChart
              data={visibleData}
              margin={{ top: 8, right: 16, bottom: 60, left: 8 }}
            >
              <defs>
                <linearGradient id="barGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COBALT} stopOpacity={0.8} />
                  <stop offset="100%" stopColor={ACCENT} stopOpacity={0.6} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(COBALT, 0.08)}
                vertical={false}
              />

              <XAxis
                dataKey="event_date"
                tick={{ fontSize: 10, fill: alpha("#000", 0.7) }}
                tickLine={false}
                axisLine={{ stroke: alpha(COBALT, 0.2) }}
                interval={tickInterval}
                angle={-45}
                textAnchor="end"
                height={70}
              />

              <YAxis
                tick={{ fontSize: 11, fill: alpha("#000", 0.7) }}
                tickLine={false}
                axisLine={{ stroke: alpha(COBALT, 0.2) }}
              />

              <RechartsTooltip content={<CustomTooltip />} />

              {showAverage && (
                <ReferenceLine
                  y={stats.avg}
                  stroke={ACCENT}
                  strokeDasharray="5 5"
                  strokeWidth={2}
                  label={{
                    value: `Avg: ${stats.avg.toFixed(1)}`,
                    fill: ACCENT,
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                />
              )}

              <Bar
                dataKey="event_count"
                fill="url(#barGradient)"
                radius={[8, 8, 0, 0]}
              />
            </RechartsBarChart>
          ) : (
            <PieChart>
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  const p = payload[0].payload as {
                    name: string;
                    value: number;
                  };

                  return (
                    <Box
                      sx={{
                        bgcolor: "rgba(33, 12, 3, 0.95)",
                        backdropFilter: "blur(12px)",
                        border: `1px solid ${alpha(ACCENT, 0.3)}`,
                        borderRadius: 2,
                        p: 1.5,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 700,
                          color: ACCENT,
                          display: "block",
                        }}
                      >
                        {p.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#fff" }}>
                        {p.value.toLocaleString()}
                      </Typography>
                    </Box>
                  );
                }}
              />
              <Legend />
              <Pie
                data={buildPieData(normalized)}
                dataKey="value"
                nameKey="name"
                outerRadius="70%"
                paddingAngle={3}
                isAnimationActive
              >
                {buildPieData(normalized).map((entry, index) => (
                  <Cell
                    key={entry.name}
                    fill={PIE_COLORS[index % PIE_COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          )}
        </ResponsiveContainer>
      </Box>

      {showBrush && normalized.length > 30 && (
        <Box
          sx={{
            width: "100%",
            height: 70,
            mt: 2,
            bgcolor: alpha("#000", 0.02),
            borderRadius: 2,
            border: `1px solid ${alpha(COBALT, 0.15)}`,
            p: 1.5,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <ResponsiveContainer>
            <LineChart data={normalized}>
              <defs>
                <linearGradient
                  id="miniLineGradient"
                  x1="0"
                  y1="0"
                  x2="1"
                  y2="0"
                >
                  <stop offset="0%" stopColor={alpha(COBALT, 0.5)} />
                  <stop offset="50%" stopColor={alpha(ACCENT, 0.5)} />
                  <stop offset="100%" stopColor={alpha(COBALT, 0.5)} />
                </linearGradient>
              </defs>
              <Line
                type="monotone"
                dataKey="event_count"
                stroke="url(#miniLineGradient)"
                strokeWidth={1.5}
                dot={false}
              />
              <Brush
                dataKey="event_date"
                height={45}
                stroke={COBALT}
                fill={alpha(COBALT, 0.05)}
                onChange={handleBrushChange}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>
      )}

      <Menu
        anchorEl={chartExportMenuAnchor}
        open={Boolean(chartExportMenuAnchor)}
        onClose={handleChartExportMenuClose}
        PaperProps={{
          sx: {
            minWidth: 200,
            boxShadow: `0 8px 32px ${alpha("#000", 0.12)}`,
            border: `1px solid ${alpha(COBALT, 0.1)}`,
          },
        }}
      >
        <MenuItem onClick={() => handleExportChart("png")}>
          <ListItemIcon>
            <ImageIcon fontSize="small" sx={{ color: "#4caf50" }} />
          </ListItemIcon>
          <ListItemText primary="PNG Image" secondary="High quality" />
        </MenuItem>

        <MenuItem onClick={() => handleExportChart("jpg")}>
          <ListItemIcon>
            <ImageIcon fontSize="small" sx={{ color: "#ff9800" }} />
          </ListItemIcon>
          <ListItemText primary="JPG Image" secondary="Compressed" />
        </MenuItem>

        <MenuItem onClick={() => handleExportChart("pdf")}>
          <ListItemIcon>
            <ArticleIcon fontSize="small" sx={{ color: "#f44336" }} />
          </ListItemIcon>
          <ListItemText primary="PDF Document" />
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default EventTrendChart;
